import React, { useState } from "react";
import {
  Package,
  Edit3,
  Save,
  X,
  Trash2,
  Truck,
  MapPin,
  GripVertical,
  Navigation,
  ChevronDown,
  ChevronRight,
  Undo2
} from "lucide-react";
import { Group, DeliveryOrder } from "../../../../redux/deliveryRouteOptimizer";
import "./index.css";

interface GroupDetailsViewProps {
  groups: Record<string, Group>;
  deliveries: DeliveryOrder[];
  deleteGroup: (groupId: string) => void;
  onSaveGroupChanges: (groupId: string, orderIds: string[]) => void;
  onOptimizePath?: (groupId: string, orderIds: string[], isManuallyOptimized: boolean) => void;
  isDeletingGroup?: boolean;
  selectedGroupsForMap?: string[];
  toggleGroupSelectionForMap?: (groupId: string) => void;
  showOnlySelectedGroups?: boolean;
  setShowOnlySelectedGroups?: (show: boolean) => void;
}

// Helper function to get optimized route order
const getOptimizedRouteOrder = (group: Group) => {
  if (group.deliveryRoute?.route && group.deliveryRoute.route.length > 0) {
    return group.deliveryRoute.route.map(routePoint => routePoint.orderId);
  }
  // Fallback to original orderIds if no optimized route
  return group.deliveryIds || [];
};

const GroupDetailsView: React.FC<GroupDetailsViewProps> = ({
  groups,
  deliveries,
  deleteGroup,
  onSaveGroupChanges,
  onOptimizePath,
  isDeletingGroup = false,
  selectedGroupsForMap = [],
  toggleGroupSelectionForMap = () => {},
  showOnlySelectedGroups = false,
  setShowOnlySelectedGroups = () => {},
}) => {
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editedOrderIds, setEditedOrderIds] = useState<string[]>([]);
  const [draggedOrderId, setDraggedOrderId] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [pendingRemovals, setPendingRemovals] = useState<Record<string, string[]>>({});

  // Filter out collection groups
  const regularGroups = Object.values(groups).filter(
    (group) => !group.isCollectionGrouped
  );

  // Toggle group expansion
  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Start editing a group (enable drag & drop and deletion)
  const startEditing = (groupId: string) => {
    setEditingGroupId(groupId);
    const optimizedOrderIds = getOptimizedRouteOrder(groups[groupId]);
    setEditedOrderIds([...optimizedOrderIds]);
    // Clear any pending removals when starting fresh
    setPendingRemovals(prev => ({
      ...prev,
      [groupId]: []
    }));
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingGroupId(null);
    setEditedOrderIds([]);
    setPendingRemovals({});
  };

  // Save changes without path optimization
  const saveChanges = (groupId: string) => {
    const removedOrderIds = pendingRemovals[groupId] || [];
    const finalOrderIds = editedOrderIds.filter(id => !removedOrderIds.includes(id));
    
    // Pass true for isManuallyOptimized since this is a manual save
    if (onOptimizePath) {
      onOptimizePath(groupId, finalOrderIds, true);
    } else {
      onSaveGroupChanges(groupId, finalOrderIds);
    }
    
    setEditingGroupId(null);
    setEditedOrderIds([]);
    setPendingRemovals(prev => {
      const newPending = { ...prev };
      delete newPending[groupId];
      return newPending;
    });
  };

  // Handle path optimization
  const handleOptimizePath = (groupId: string) => {
    if (onOptimizePath && hasManualChanges(groupId)) {
      const removedOrderIds = pendingRemovals[groupId] || [];
      const finalOrderIds = editedOrderIds.filter(id => !removedOrderIds.includes(id));
      
      // Pass false for isManuallyOptimized since this is the path optimized button
      onOptimizePath(groupId, finalOrderIds, false);
      setEditingGroupId(null);
      setEditedOrderIds([]);
      setPendingRemovals(prev => {
        const newPending = { ...prev };
        delete newPending[groupId];
        return newPending;
      });
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, orderId: string) => {
    setDraggedOrderId(orderId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (!draggedOrderId) return;

    const draggedIndex = editedOrderIds.indexOf(draggedOrderId);
    if (draggedIndex === -1) return;

    const newOrderIds = [...editedOrderIds];
    const [removed] = newOrderIds.splice(draggedIndex, 1);
    newOrderIds.splice(targetIndex, 0, removed);

    setEditedOrderIds(newOrderIds);
    setDraggedOrderId(null);
  };

  const handleDragEnd = () => {
    setDraggedOrderId(null);
  };

  // Handle removing an order from group (pending removal)
  const handlePendingRemove = (deliveryId: string, groupId: string) => {
    setPendingRemovals(prev => ({
      ...prev,
      [groupId]: [...(prev[groupId] || []), deliveryId]
    }));
  };

  // Handle undoing a pending removal
  const handleUndoRemove = (deliveryId: string, groupId: string) => {
    setPendingRemovals(prev => ({
      ...prev,
      [groupId]: (prev[groupId] || []).filter(id => id !== deliveryId)
    }));
  };

  // Check if order sequence has been manually changed
  const hasManualChanges = (groupId: string) => {
    if (!editingGroupId || editingGroupId !== groupId) return false;
    
    const originalOrderIds = getOptimizedRouteOrder(groups[groupId]);
    const hasOrderChanges = JSON.stringify(editedOrderIds) !== JSON.stringify(originalOrderIds);
    const hasRemovalChanges = (pendingRemovals[groupId] || []).length > 0;
    
    return hasOrderChanges || hasRemovalChanges;
  };

  // Check if a delivery is marked for removal
  const isMarkedForRemoval = (deliveryId: string, groupId: string) => {
    return (pendingRemovals[groupId] || []).includes(deliveryId);
  };

  const getDeliveryById = (deliveryId: string) => {
    return deliveries.find(d => d.id === deliveryId);
  };

  // Format distance for display
  const formatDistance = (distanceInKm: number): string => {
    return `${distanceInKm.toFixed(2)} km`;
  };

  // Handle individual group selection
  const handleGroupSelection = (groupId: string) => {
    toggleGroupSelectionForMap(groupId);
    if (!showOnlySelectedGroups && selectedGroupsForMap.length === 0) {
      setShowOnlySelectedGroups(true);
    }
  };

  if (regularGroups.length === 0) {
    return (
      <div className="group-details-view__empty-state">
        <Package size={48} className="group-details-view__empty-icon" />
        <h3 className="group-details-view__empty-title">No Groups Created</h3>
        <p className="group-details-view__empty-description">
          Create groups to organize your deliveries
        </p>
      </div>
    );
  }

  return (
    <div className="group-details-view">
      {/* Header with Controls */}
     
      <div className="group-details-view__content">
        <div className="group-details-view__groups-list">
          {regularGroups.map((group) => {
            const optimizedOrderIds = getOptimizedRouteOrder(group);
            const isEditing = editingGroupId === group.id;
            const displayOrderIds = isEditing ? editedOrderIds : optimizedOrderIds;
            const hasChanges = hasManualChanges(group.id);
            const isExpanded = expandedGroups[group.id];
            const isSelectedForMap = selectedGroupsForMap.includes(group.id);
            const hasDeliveryRoute = group.deliveryRoute && group.deliveryRoute.distanceInKm > 0;
            const pendingRemovalsCount = (pendingRemovals[group.id] || []).length;

            return (
              <div
                key={group.id}
                className={`group-details-card ${isSelectedForMap ? 'group-details-card--selected' : ''}`}
                style={{ borderLeftColor: group.color }}
              >
                {/* Group Header */}
                <div 
                  className="group-details-card__header"
                  onClick={() => toggleGroupExpansion(group.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="group-details-card__info">
                    {/* Checkbox for map selection */}
                    <div 
                      className="group-details-card__checkbox-container"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelectedForMap}
                        onChange={() => handleGroupSelection(group.id)}
                        className="group-details-card__checkbox-input"
                      />
                    </div>

                    {/* Expand/Collapse Icon */}
                    <div className="group-details-card__expand-icon">
                      {isExpanded ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                    </div>

                    <div className="group-details-card__title-section">
                      <div className="group-details-card__name-row">
                        <h3 className={`group-details-card__name ${isSelectedForMap ? 'group-details-card__name--selected' : ''}`}>
                          {group.name}
                        </h3>
                        
                        <div className="group-details-card__badges">
                          {group.isManuallyOptimizedPath && (
                            <span className="group-details-card__optimized-badge">
                              <Navigation size={14} />
                              Optimized Path
                            </span>
                          )}
                          {(hasChanges || pendingRemovalsCount > 0) && (
                            <span className="group-details-card__changes-indicator">
                              {pendingRemovalsCount > 0 ? `${pendingRemovalsCount} pending` : 'Order Changed'}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Group Summary Information */}
                      <div className="group-details-card__summary">
                        
                        {/* Distance Information */}
                        {hasDeliveryRoute && (
                          <div className="group-details-card__distance-section">
                            <MapPin size={14} />
                            <span className="group-details-card__distance">
                              {formatDistance(group.deliveryRoute!.distanceInKm)}
                            </span>
                          </div>
                        )}
                        
                        {/* Show Assigned Cart Information */}
                        {group.cart && (
                          <div className="group-details-card__cart-info">
                            <Truck size={14} />
                            <span className="group-details-card__cart-name">
                              {group.cart.displayName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div 
                    className="group-details-card__actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!isEditing ? (
                      <>
                        <button
                          onClick={() => startEditing(group.id)}
                          className="group-details-card__edit-button"
                          title="Edit Orders"
                        >
                          <Edit3 size={16} />
                          Edit Orders
                        </button>
                        <button
                          onClick={() => deleteGroup(group.id)}
                          disabled={isDeletingGroup}
                          className="group-details-card__delete-button"
                          title="Delete group"
                        >
                          {isDeletingGroup ? (
                            <div className="group-details-card__delete-spinner"></div>
                          ) : (
                            <Trash2 size={16} />
                          )}
                        </button>
                      </>
                    ) : (
                      <div className="group-details-card__edit-controls">
                        <button
                          onClick={() => handleOptimizePath(group.id)}
                          disabled={!hasChanges}
                          className={`group-details-card__optimize-button ${
                            !hasChanges ? 'group-details-card__optimize-button--disabled' : ''
                          }`}
                          title="Save optimized path"
                        >
                          <Navigation size={16} />
                          Path Optimized
                        </button>
                        <button
                          onClick={() => saveChanges(group.id)}
                          className="group-details-card__save-button"
                          title="Save changes"
                        >
                          <Save size={16} />
                          Save
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="group-details-card__cancel-button"
                          title="Cancel changes"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Group Deliveries - Collapsible Content */}
                {isExpanded && (
                  <div className="group-details-card__deliveries">
                    {displayOrderIds.length === 0 ? (
                      <div className="group-details-card__empty-deliveries">
                        <Package size={32} />
                        <p>No deliveries in this group</p>
                      </div>
                    ) : (
                      <>
                        <div className="group-details-card__delivery-list">
                          {displayOrderIds.map((deliveryId: any, index: any) => {
                            const delivery = getDeliveryById(deliveryId);
                            if (!delivery) return null;

                            const isRemoved = isMarkedForRemoval(deliveryId, group.id);

                            return (
                              <div
                                key={deliveryId}
                                className={`group-delivery-item ${
                                  isEditing ? 'group-delivery-item--editable' : ''
                                } ${
                                  draggedOrderId === deliveryId ? 'group-delivery-item--dragging' : ''
                                } ${
                                  isRemoved ? 'group-delivery-item--removed' : ''
                                }`}
                                draggable={isEditing && !isRemoved}
                                onDragStart={(e) => !isRemoved && handleDragStart(e, deliveryId)}
                                onDragOver={(e) => !isRemoved && handleDragOver(e, index)}
                                onDrop={(e) => !isRemoved && handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                              >
                                {isEditing && !isRemoved && (
                                  <div className="group-delivery-item__drag-handle">
                                    <GripVertical size={16} />
                                  </div>
                                )}
                                
                                <div className="group-delivery-item__content">
                                  <div className="group-delivery-item__header">
                                    <div className="group-delivery-item__reference">
                                      {delivery.orderReference}
                                      {isRemoved && (
                                        <span className="group-delivery-item__removed-badge">
                                          Marked for removal
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="group-delivery-item__details">
                                    <div className="group-delivery-item__customer">
                                      <strong>Customer:</strong> {delivery.customer?.displayName || delivery.organization?.displayName || 'No Customer'}
                                    </div>
                                    <div className="group-delivery-item__address">
                                      <strong>Address:</strong> {delivery.deliveryDetails.address}
                                    </div>
                                    {delivery.phoneNumber && (
                                      <div className="group-delivery-item__phone">
                                        <strong>Phone:</strong> {delivery.phoneNumber}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="group-delivery-item__actions">
                                  {isEditing ? (
                                    isRemoved ? (
                                      <button
                                        onClick={() => handleUndoRemove(deliveryId, group.id)}
                                        className="group-delivery-item__undo-button"
                                        title="Undo removal"
                                      >
                                        <Undo2 size={14} />
                                        Undo
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handlePendingRemove(deliveryId, group.id)}
                                        className="group-delivery-item__remove-button"
                                        title="Remove from group"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    )
                                  ) : (
                                    <div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GroupDetailsView;